import React from 'react';
import { useTranslation } from 'react-i18next';
import parseRelatedTopics from '../../../../../../helpers/effectReports/parseRelatedTopics';
import { HIGH_SCORE, LOW_SCORE, MEDIUM_SCORE } from '../../../../../../helpers/helpers';
import { useReportContext } from '../../../../../../hooks/useReport';
import { Analysis } from '../../../../../../services/reports';
import { Text } from '../ReportTimestamps.styles';
import { ColorBlock, ColorBlocks } from './ValueColorBlocks.styles';

interface ThresholdColorBlocksProps {
  shape: 'square' | 'circle';
  direction: 'row' | 'column';
  flattened?: boolean;
  thresholdValues: {
    low?: number;
    medium?: number;
    high?: number;
  };
}

interface ValueColorBlocksProps {
  shape: 'square' | 'circle';
  direction: 'row' | 'column';
  flattened?: boolean;
  data: Analysis;
}

export const ThresholdColorBlocks: React.FC<ThresholdColorBlocksProps> = ({
  shape,
  direction,
  flattened = false,
  thresholdValues,
}) => {
  const { t } = useTranslation();
  const { low = LOW_SCORE, medium = MEDIUM_SCORE, high = HIGH_SCORE } = thresholdValues;

  return (
    <ColorBlocks direction={direction}>
      <div>
        <ColorBlock color="#E37475" shape={shape} flattened={flattened}></ColorBlock>
        <Text>{`${t('report.effect.timestamps.veryLowScore')} (< ${low})`}</Text>
      </div>
      <div>
        <ColorBlock color="#EEB89A" shape={shape} flattened={flattened}></ColorBlock>
        <Text>{`${t('report.effect.timestamps.lowScore')} (${low} - ${medium - 0.1})`}</Text>
      </div>
      <div>
        <ColorBlock color="#2A7FAA" shape={shape} flattened={flattened}></ColorBlock>
        <Text>{`${t('report.effect.timestamps.normalScore')} (${medium} - ${high})`}</Text>
      </div>
      <div>
        <ColorBlock color="#0DAE89" shape={shape} flattened={flattened}></ColorBlock>
        <Text>{`${t('report.effect.timestamps.highScore')} (> ${high})`}</Text>
      </div>
    </ColorBlocks>
  );
};

const ValueColorBlocks: React.FC<ValueColorBlocksProps> = ({
  shape,
  direction,
  flattened = false,
  data,
}) => {
  const { effectData } = useReportContext();
  const parsedData = parseRelatedTopics(data, effectData);
  const thresholdValues = parsedData[0]?.threshold_values;

  if (!thresholdValues) {
    return null;
  }

  return (
    <ThresholdColorBlocks
      shape={shape}
      direction={direction}
      flattened={flattened}
      thresholdValues={thresholdValues}
    />
  );
};

export default ValueColorBlocks;
